import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Archive, Undo2, Trash2, X, Users, Trophy } from "lucide-react";
import { toast } from "sonner";
import BulkWeaponDialog from "@/components/BulkWeaponDialog";

const REASONS = [
  { v: "duplicato", l: "Duplicato" },
  { v: "importazione errata", l: "Importazione errata" },
  { v: "atleta non più attivo", l: "Atleta non più attivo" },
  { v: "scheda di prova", l: "Scheda di prova" },
  { v: "altro", l: "Altro" },
];

export default function BulkAthleteActionsBar({ selectedIds, onCleared, onDone, isArchivedView = false }) {
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [reason, setReason] = useState("duplicato");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteCheck, setDeleteCheck] = useState(null);
  const [finalOpen, setFinalOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // v1.7.5 · Bulk set Attività
  const [activityConfirm, setActivityConfirm] = useState(null); // 'agonista' | 'non_agonista' | '__unset' | null

  const n = selectedIds.length;
  if (n === 0) return null;

  const doSetActivity = async () => {
    if (!activityConfirm) return;
    setBusy(true);
    try {
      const payload = { ids: selectedIds, activity_type: activityConfirm === "__unset" ? null : activityConfirm };
      const { data } = await api.post("/athletes/bulk/set-activity-type", payload);
      const label = activityConfirm === "__unset" ? "Non impostato" : (activityConfirm === "agonista" ? "Agonista" : "Non agonista");
      toast.success(`${data.updated} atleti impostati su "${label}"`);
      setActivityConfirm(null); onCleared(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const doArchive = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/athletes/bulk/archive", { ids: selectedIds, reason });
      toast.success(`${data.archived} schede archiviate`);
      setArchiveOpen(false); onCleared(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const doUnarchive = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/athletes/bulk/unarchive", { ids: selectedIds });
      toast.success(`${data.reactivated} schede riattivate`);
      onCleared(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const openDelete = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/athletes/bulk/delete-check", { ids: selectedIds });
      setDeleteCheck(data);
      setDeleteOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/athletes/bulk/delete", { ids: selectedIds, confirm: "ELIMINA" });
      toast.success(`${data.deleted} schede eliminate · ${data.protected} protette (con storico)`);
      setDeleteOpen(false); setFinalOpen(false); onCleared(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div data-testid="bulk-bar" className="sticky top-0 z-10 bg-black text-white rounded-md px-4 py-2 flex items-center gap-3 flex-wrap shadow-lg">
        <div className="flex items-center gap-2"><Users className="w-4 h-4" /><strong className="font-mono">{n}</strong> selezionati</div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {/* v1.7.5 · Imposta Attività (dropdown compatto stile-Excel) */}
          <div className="inline-flex items-stretch bg-white/10 rounded overflow-hidden" data-testid="bulk-activity-group">
            <span className="inline-flex items-center px-2 text-[11px] uppercase tracking-wider"><Trophy className="w-3.5 h-3.5 mr-1" />Attività</span>
            <button
              data-testid="bulk-activity-agonista"
              disabled={busy}
              onClick={() => setActivityConfirm("agonista")}
              className="px-2 text-xs bg-white text-black hover:bg-emerald-100 border-l border-white/10 disabled:opacity-50"
            >Agonista</button>
            <button
              data-testid="bulk-activity-non-agonista"
              disabled={busy}
              onClick={() => setActivityConfirm("non_agonista")}
              className="px-2 text-xs bg-white text-black hover:bg-zinc-100 border-l border-white/10 disabled:opacity-50"
            >Non ag.</button>
            <button
              data-testid="bulk-activity-unset"
              disabled={busy}
              onClick={() => setActivityConfirm("__unset")}
              className="px-2 text-xs bg-white text-black hover:bg-zinc-100 border-l border-white/10 disabled:opacity-50 italic"
            >— Non imp.</button>
          </div>
          {!isArchivedView && <BulkWeaponDialog selectedIds={selectedIds} onDone={() => { onCleared(); onDone(); }} />}
          {isArchivedView ? (
            <Button size="sm" variant="secondary" data-testid="bulk-unarchive" onClick={doUnarchive} disabled={busy}><Undo2 className="w-3.5 h-3.5 mr-1" />Riattiva</Button>
          ) : (
            <Button size="sm" variant="secondary" data-testid="bulk-archive" onClick={() => setArchiveOpen(true)} disabled={busy}><Archive className="w-3.5 h-3.5 mr-1" />Archivia</Button>
          )}
          <Button size="sm" variant="destructive" data-testid="bulk-delete" onClick={openDelete} disabled={busy}><Trash2 className="w-3.5 h-3.5 mr-1" />Elimina</Button>
          <Button size="sm" variant="ghost" className="text-white hover:bg-zinc-800" onClick={onCleared} data-testid="bulk-clear"><X className="w-3.5 h-3.5" /></Button>
        </div>
      </div>

      <AlertDialog open={activityConfirm !== null} onOpenChange={(o) => !o && setActivityConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Impostare Attività per {n} atleti?</AlertDialogTitle>
            <AlertDialogDescription>
              {activityConfirm === "agonista" && <>Verranno impostati come <strong>Agonista</strong> {n} atleti selezionati.</>}
              {activityConfirm === "non_agonista" && <>Verranno impostati come <strong>Non agonista</strong> {n} atleti selezionati.</>}
              {activityConfirm === "__unset" && <>Verranno riportati a <strong>Non impostato</strong> (campo rimosso) {n} atleti selezionati.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="bulk-activity-confirm" onClick={doSetActivity} disabled={busy} className="bg-black hover:bg-zinc-800">Conferma</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Archivia {n} schede</DialogTitle><DialogDescription>Le schede archiviate conservano storico e dati collegati. Sono nascoste dagli elenchi correnti ma possono essere riattivate.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <label className="text-xs uppercase tracking-wider text-zinc-500">Motivo</label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="archive-reason"><SelectValue /></SelectTrigger>
              <SelectContent>{REASONS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveOpen(false)}>Annulla</Button>
            <Button data-testid="confirm-archive" className="bg-black hover:bg-zinc-800" onClick={doArchive} disabled={busy}>Archivia {n}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Elimina definitivamente</DialogTitle><DialogDescription>Le schede con storico (presenze, test, gare, attività, infortuni) sono protette e verranno segnalate. Solo le schede senza record collegati saranno cancellate.</DialogDescription></DialogHeader>
          {deleteCheck && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="border rounded p-2 bg-emerald-50 border-emerald-200"><div className="text-xs uppercase text-emerald-700">Eliminabili</div><div className="text-2xl font-bold">{deleteCheck.deletable_count}</div></div>
                <div className="border rounded p-2 bg-amber-50 border-amber-200"><div className="text-xs uppercase text-amber-700">Protette (con storico)</div><div className="text-2xl font-bold">{deleteCheck.protected_count}</div></div>
              </div>
              <div className="max-h-60 overflow-y-auto border rounded text-xs">
                {deleteCheck.results.map((r) => (
                  <div key={r.id} className="flex items-center gap-2 px-3 py-1.5 border-b border-zinc-100">
                    <span className="flex-1">{r.name}</span>
                    {r.deletable
                      ? <span className="text-emerald-700 font-mono text-[10px] uppercase">eliminabile</span>
                      : <span className="text-amber-700 font-mono text-[10px]">{r.total_dependents} record</span>}
                  </div>
                ))}
              </div>
              <p className="text-xs text-zinc-500">Operazione <strong>diversa dall'archiviazione</strong>: le schede eliminabili verranno rimosse dall'anagrafica CSP Lab.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" data-testid="bulk-delete-cancel-1" onClick={() => setDeleteOpen(false)}>Annulla</Button>
            <Button data-testid="bulk-delete-continue" className="bg-black hover:bg-zinc-800" onClick={() => setFinalOpen(true)} disabled={busy || !deleteCheck || deleteCheck.deletable_count === 0}>Continua</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={finalOpen} onOpenChange={setFinalOpen}>
        <AlertDialogContent data-testid="bulk-delete-final">
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma finale</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare definitivamente {deleteCheck?.deletable_count || 0} schede:{" "}
              <strong>{(deleteCheck?.results || []).filter((r) => r.deletable).map((r) => r.name).join(", ")}</strong>. L'azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-cancel-2" disabled={busy}>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete" onClick={(e) => { e.preventDefault(); doDelete(); }} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white">Elimina definitivamente</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
